// client/src/components/pages/Geofencing/zoneEditor/zoneEditorState.ts
// The zone editor's state machine (ST-00239): which tool is active, the shape
// being drawn, the committed shape, a transient drag preview, the selected
// corner, and an undo/redo history of every committed change. A plain reducer —
// the map component only turns pointer events into actions — so every
// interaction rule is unit-tested without a map.
import {
  MIN_VERTICES,
  haversineM,
  rectangleRing,
  removeVertex,
  type LngLat,
  type ZoneShape,
} from './zoneGeometry';

export type DrawTool = 'polygon' | 'rectangle' | 'circle';
export type EditorTool = 'edit' | DrawTool;

export type Draft =
  | { kind: 'polygon'; points: LngLat[]; redo: LngLat[] }
  | { kind: 'rectangle'; start: LngLat; end: LngLat | null }
  | { kind: 'circle'; center: LngLat; edge: LngLat | null };

export interface HistoryEntry {
  /** The shape to go back (or forward) to. */
  shape: ZoneShape | null;
  /** What the step did, e.g. "Move corner" (for the Undo / Redo tooltips). */
  label: string;
}

export interface EditorState {
  tool: EditorTool;
  /** The committed shape (what Save sends). */
  shape: ZoneShape | null;
  /** A drag in progress (vertex, midpoint, move, resize); shown instead of `shape`. */
  preview: ZoneShape | null;
  draft: Draft | null;
  /** Selected corner (polygon), for Delete / arrow nudging. */
  selected: number | null;
  past: HistoryEntry[];
  future: HistoryEntry[];
  /** Consecutive commits with the same key inside the window merge into one undo step. */
  lastMerge: { key: string; at: number } | null;
  /** A short message for the hint bar ("A zone needs at least 3 corners"). */
  notice: string | null;
}

export type EditorAction =
  | { type: 'tool'; tool: EditorTool }
  | { type: 'draftPoint'; point: LngLat }
  | { type: 'draftUndo' }
  | { type: 'draftRedo' }
  | { type: 'draftStart'; draft: Draft }
  | { type: 'draftMove'; point: LngLat }
  | { type: 'finishDraft' }
  | { type: 'cancelDraft' }
  | { type: 'clearDraft' }
  | { type: 'preview'; shape: ZoneShape | null }
  | { type: 'commit'; shape: ZoneShape; label: string; mergeKey?: string; at?: number }
  | { type: 'select'; index: number | null }
  | { type: 'deleteVertex'; index: number }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'reset'; shape: ZoneShape | null }
  | { type: 'clear' }
  | { type: 'notice'; message: string | null };

const HISTORY_LIMIT = 200;
const MERGE_WINDOW_MS = 1200;
/** Smallest rectangle side / circle radius a drag may finish with (metres). */
export const MIN_DRAG_M = 1;

export function initialEditorState(shape: ZoneShape | null): EditorState {
  return {
    tool: shape ? 'edit' : 'polygon',
    shape,
    preview: null,
    draft: null,
    selected: null,
    past: [],
    future: [],
    lastMerge: null,
    notice: null,
  };
}

const TOOL_LABEL: Record<DrawTool, string> = {
  polygon: 'Draw polygon',
  rectangle: 'Draw rectangle',
  circle: 'Draw circle',
};

function commit(
  state: EditorState,
  shape: ZoneShape | null,
  label: string,
  mergeKey?: string,
  at?: number
): EditorState {
  const merge =
    mergeKey &&
    at != null &&
    state.lastMerge &&
    state.lastMerge.key === mergeKey &&
    at - state.lastMerge.at <= MERGE_WINDOW_MS &&
    state.past.length > 0;
  const past = merge
    ? state.past
    : [...state.past, { shape: state.shape, label }].slice(-HISTORY_LIMIT);
  return {
    ...state,
    shape,
    preview: null,
    past,
    future: [],
    lastMerge: mergeKey && at != null ? { key: mergeKey, at } : null,
    notice: null,
  };
}

/** The shape a finished draft makes, or a reason it cannot finish yet. */
export function shapeFromDraft(draft: Draft): { shape: ZoneShape } | { reason: string } {
  if (draft.kind === 'polygon') {
    return draft.points.length >= MIN_VERTICES
      ? { shape: { kind: 'polygon', ring: draft.points.slice() } }
      : { reason: 'Place at least 3 corners first.' };
  }
  if (draft.kind === 'rectangle') {
    if (!draft.end) return { reason: 'Click the opposite corner.' };
    const w = haversineM(draft.start, [draft.end[0], draft.start[1]]);
    const h = haversineM(draft.start, [draft.start[0], draft.end[1]]);
    return w >= MIN_DRAG_M && h >= MIN_DRAG_M
      ? { shape: { kind: 'polygon', ring: rectangleRing(draft.start, draft.end) } }
      : { reason: 'Drag further to make the rectangle bigger.' };
  }
  if (!draft.edge) return { reason: 'Click where the edge of the circle should be.' };
  const r = Math.round(haversineM(draft.center, draft.edge));
  return r >= MIN_DRAG_M
    ? { shape: { kind: 'circle', center: draft.center, radiusM: r } }
    : { reason: 'Drag further to make the circle bigger.' };
}

export function zoneEditorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'tool':
      return {
        ...state,
        tool: action.tool,
        draft: null,
        preview: null,
        selected: null,
        notice: null,
      };

    case 'draftPoint': {
      if (state.tool !== 'polygon') return state;
      const d = state.draft?.kind === 'polygon' ? state.draft : null;
      const points = d ? d.points : [];
      const last = points[points.length - 1];
      if (last && last[0] === action.point[0] && last[1] === action.point[1]) return state;
      return {
        ...state,
        draft: { kind: 'polygon', points: [...points, action.point], redo: [] },
        notice: null,
      };
    }

    case 'draftUndo': {
      const d = state.draft;
      if (!d) return state;
      if (d.kind !== 'polygon') return { ...state, draft: null };
      const points = d.points.slice(0, -1);
      const removed = d.points[d.points.length - 1];
      return {
        ...state,
        draft:
          points.length || d.redo.length || removed
            ? { kind: 'polygon', points, redo: removed ? [...d.redo, removed] : d.redo }
            : null,
      };
    }

    case 'draftRedo': {
      const d = state.draft;
      if (!d || d.kind !== 'polygon' || !d.redo.length) return state;
      const next = d.redo[d.redo.length - 1]!;
      return {
        ...state,
        draft: { kind: 'polygon', points: [...d.points, next], redo: d.redo.slice(0, -1) },
      };
    }

    case 'draftStart':
      return { ...state, draft: action.draft, selected: null, notice: null };

    case 'draftMove': {
      const d = state.draft;
      if (!d) return state;
      if (d.kind === 'rectangle') return { ...state, draft: { ...d, end: action.point } };
      if (d.kind === 'circle') return { ...state, draft: { ...d, edge: action.point } };
      return state;
    }

    case 'finishDraft': {
      const d = state.draft;
      if (!d) return state;
      const res = shapeFromDraft(d);
      if ('reason' in res) return { ...state, notice: res.reason };
      const label = TOOL_LABEL[d.kind];
      return { ...commit(state, res.shape, label), tool: 'edit', draft: null, selected: null };
    }

    case 'cancelDraft':
      return { ...state, draft: null, tool: 'edit', notice: null };

    case 'clearDraft':
      return { ...state, draft: null };

    case 'preview':
      return { ...state, preview: action.shape };

    case 'commit':
      return commit(state, action.shape, action.label, action.mergeKey, action.at);

    case 'select':
      return { ...state, selected: action.index, notice: null };

    case 'deleteVertex': {
      const shape = state.shape;
      if (!shape || shape.kind !== 'polygon') return state;
      const ring = removeVertex(shape.ring, action.index);
      if (!ring) {
        return {
          ...state,
          notice: 'A zone needs at least 3 corners, so this one cannot be removed.',
        };
      }
      return { ...commit(state, { kind: 'polygon', ring }, 'Delete corner'), selected: null };
    }

    case 'undo': {
      // While drawing a polygon, undo takes back the last corner.
      if (state.draft?.kind === 'polygon') return zoneEditorReducer(state, { type: 'draftUndo' });
      if (state.draft) return { ...state, draft: null };
      const entry = state.past[state.past.length - 1];
      if (!entry) return state;
      return {
        ...state,
        shape: entry.shape,
        preview: null,
        selected: null,
        past: state.past.slice(0, -1),
        future: [...state.future, { shape: state.shape, label: entry.label }],
        lastMerge: null,
        notice: null,
      };
    }

    case 'redo': {
      if (state.draft?.kind === 'polygon' && state.draft.redo.length) {
        return zoneEditorReducer(state, { type: 'draftRedo' });
      }
      const entry = state.future[state.future.length - 1];
      if (!entry || state.draft) return state;
      return {
        ...state,
        shape: entry.shape,
        preview: null,
        selected: null,
        future: state.future.slice(0, -1),
        past: [...state.past, { shape: state.shape, label: entry.label }],
        lastMerge: null,
        notice: null,
      };
    }

    case 'reset':
      return {
        ...commit(state, action.shape, 'Reset to saved'),
        tool: action.shape ? 'edit' : 'polygon',
        draft: null,
        selected: null,
      };

    case 'clear':
      if (!state.shape) return { ...state, tool: 'polygon', draft: null, selected: null };
      return {
        ...commit(state, null, 'Clear shape'),
        tool: 'polygon',
        draft: null,
        selected: null,
      };

    case 'notice':
      return { ...state, notice: action.message };

    default:
      return state;
  }
}

/** Undo / Redo availability and labels for the toolbar. */
export function historyInfo(state: EditorState): {
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
} {
  const drawing = state.draft?.kind === 'polygon';
  const draftPoints = drawing && state.draft?.kind === 'polygon' ? state.draft.points.length : 0;
  const draftRedo = drawing && state.draft?.kind === 'polygon' ? state.draft.redo.length : 0;
  const lastPast = state.past[state.past.length - 1];
  const lastFuture = state.future[state.future.length - 1];
  return {
    canUndo: drawing ? draftPoints > 0 : !!state.draft || !!lastPast,
    canRedo: drawing ? draftRedo > 0 : !state.draft && !!lastFuture,
    undoLabel: drawing ? 'Remove last corner' : lastPast ? lastPast.label : null,
    redoLabel: drawing
      ? draftRedo
        ? 'Put corner back'
        : null
      : lastFuture
        ? lastFuture.label
        : null,
  };
}

export interface HintContext {
  /** Touch screen (no hover, no keyboard): "tap" wording, no key names. */
  touch: boolean;
  /** Saving is blocked by a geometry problem (the message is shown instead). */
  problem: string | null;
}

/**
 * What the next click / key does, for the always-visible hint bar. Plain words,
 * one line, adapted to the tool and the drawing / editing state.
 */
export function hintFor(
  state: EditorState,
  ctx: HintContext
): { text: string; tone: 'info' | 'warn' } {
  if (state.notice) return { text: state.notice, tone: 'warn' };
  const tap = ctx.touch ? 'Tap' : 'Click';
  const d = state.draft;
  if (state.tool === 'polygon') {
    const n = d?.kind === 'polygon' ? d.points.length : 0;
    if (n === 0) {
      return {
        text: `${tap} the map to place the first corner.${ctx.touch ? '' : ' Esc cancels.'}`,
        tone: 'info',
      };
    }
    if (n < 3) {
      return {
        text: ctx.touch
          ? `${tap} to place corner ${n + 1}. Undo removes the last one.`
          : `Click to place corner ${n + 1}. Backspace removes the last one, Esc cancels.`,
        tone: 'info',
      };
    }
    return {
      text: ctx.touch
        ? `${n} corners. Tap to add more, or tap the first corner or Finish to close the shape.`
        : `${n} corners. Click to add more. Click the first corner, double-click or press Enter to finish.`,
      tone: 'info',
    };
  }
  if (state.tool === 'rectangle') {
    if (d?.kind === 'rectangle') {
      return { text: `${tap} or release at the opposite corner.`, tone: 'info' };
    }
    return {
      text: ctx.touch
        ? 'Press and drag to draw a rectangle, or tap two opposite corners.'
        : 'Press and drag to draw a rectangle, or click two opposite corners.',
      tone: 'info',
    };
  }
  if (state.tool === 'circle') {
    if (d?.kind === 'circle') {
      return { text: `${tap} or release where the edge of the circle should be.`, tone: 'info' };
    }
    return {
      text: ctx.touch
        ? 'Press at the centre and drag out to the edge, or tap the centre and then the edge.'
        : 'Press at the centre and drag out to the edge, or click the centre and then the edge.',
      tone: 'info',
    };
  }
  if (ctx.problem) return { text: ctx.problem, tone: 'warn' };
  const shape = state.shape;
  if (!shape) {
    return {
      text: 'Choose Polygon, Rectangle or Circle to draw the zone, or import a shape.',
      tone: 'info',
    };
  }
  if (shape.kind === 'circle') {
    return {
      text: 'Drag the centre to move the circle and the edge handle to resize it, or type the radius.',
      tone: 'info',
    };
  }
  if (state.selected != null) {
    return {
      text: ctx.touch
        ? `Corner ${state.selected + 1} selected. Drag it, or press and hold it to delete it.`
        : `Corner ${state.selected + 1} selected. Delete removes it, arrow keys nudge it, Esc deselects.`,
      tone: 'info',
    };
  }
  return {
    text: ctx.touch
      ? 'Drag a corner to move it, drag a small dot to add a corner, press and hold a corner to delete it.'
      : 'Drag a corner to move it, drag a small dot to add a corner, click a corner and press Delete to remove it.',
    tone: 'info',
  };
}
